import { createContext, useContext } from "react";
import type { OtpJourneyMetadata } from "@/lib/auth/otp-journey";
import type { User, Session } from "@supabase/supabase-js";
import type { GuestCheckoutDetails } from "@workspace/api-client-react";

export interface UserProfile {
  guest_email_confirmed?: boolean;
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  profile_image: string | null;
  admin_role: number;
  admin_role_id?: number | null;
  admin_role_key?: string | null;
  admin_role_name?: string | null;
  permissions?: string[];
  renter_verified: boolean;
  platform_origin: string | null;
  country_code?: string | null;
  city: string | null;
  about: string | null;
  created_at: string | null;
  user_type: number | null;
  is_host: boolean;
  marketing_opt_in?: boolean | null;
  terms_accepted_at?: string | null;
  email_is_relayed?: boolean;
  language?: string | null;
  locale?: string | null;
  preferred_currency?: string | null;
  currency?: string | null;
  host_tour_seen_at?: string | null;
  earnings_tour_seen_at?: string | null;
  reviews_tour_seen_at?: string | null;
  is_complete?: boolean;
  missing_profile_fields?: Array<"first_name" | "last_name" | "email" | "marketing_opt_in" | "terms_accepted_at">;
  // Task #4640 — server-derived cross-brand origin flag. True when this user
  // is a DriveShare-origin account (migrated or native historic DriveShare).
  // Computed server-side in `decorateProfile`; the client never sees the
  // underlying source_id/platform_origin logic. Gates the post-login
  // driveshare.com nudge modal.
  isDriveShareOrigin?: boolean;
  // Task #5897 — account suspension flag (`users.is_active`). The server still
  // 200s /auth/ensure-user for a suspended existing user, so AuthProvider reads
  // this to surface the suspension gracefully (sign the session out + toast the
  // intended message) instead of loading a half-usable state whose background
  // authed calls all 403 with `account_suspended`.
  is_active?: boolean | null;
}

// Task #2451 — Account-restore state. AuthProvider sets this when the
// server's /auth/ensure-user response carries `account_status: "soft_deleted"`
// (i.e. the user is re-registering with an email that matches a soft-
// deleted row still inside the 30-day GDPR retention window). The
// AuthModal renders a "Restore your account" CTA off this; calling
// `restoreAccount` clears the soft-delete markers server-side and
// re-fetches the profile.
export interface SoftDeletedInfo {
  deletedAt: string;
  scheduledErasureAt: string;
}

export interface AuthState {
  /** Verified account status, separate from a linked guest booking's payer. */
  linkedAccountPhoneVerified?: boolean | null;
  startGuestCheckout?: (details: GuestCheckoutDetails) => Promise<{ error: Error | null; authUserId?: string }>;
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  profileError: boolean;
  softDeletedInfo: SoftDeletedInfo | null;
  // Task #5287 — true when the principal is authenticated but has no local
  // public.users row yet (web defers creation until the profile-completion
  // dialog submits name + Terms). The completion dialog mounts off this even
  // though `profile` is still null, and it must NOT be treated as profileError.
  needsProfile: boolean;
  restoreAccount: () => Promise<{ error: Error | null }>;
  // Optimistically merge a partial update into the in-memory profile
  // WITHOUT a server round-trip. Used by flows that persist a profile
  // field via a fire-and-forget PATCH (e.g. the host welcome tour's
  // `host_tour_seen_at`) and need the local `profile` to reflect the
  // change immediately so a remount can't read the stale pre-PATCH
  // value. No-ops when there is no profile loaded.
  patchProfile: (patch: Partial<UserProfile>) => void;
  // Task #4155 — overlay the *effective* (impersonated) host status onto the
  // profile while an admin is impersonating a user. Pass a boolean to override
  // `profile.is_host` (so the Header host/guest toggle, My Garage, Host
  // Dashboard nav, and RequireHost routes reflect the impersonated host), or
  // `null` to clear the override and revert to the admin's own host status.
  // Admin-identity fields (admin_role, permissions, isAdmin) are never touched
  // by this — only `is_host` is overlaid, and nothing is written to the
  // admin's cached profile, so there is no cache bleed in either direction.
  setHostOverride: (isHost: boolean | null) => void;
  isAdmin: boolean;
  adminRole: number;
  permissions: string[];
  hasPermission: (perm: string) => boolean;
  isOwner: boolean;
  retryProfile: () => void;
  signOut: () => Promise<void>;
  signOutForDriverInvitation: (continuationPath: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  // Task #3526 — in-place admin re-elevation. Challenges the enrolled TOTP
  // factor, verifies the code, and adopts the aal2 refresh token into the
  // HttpOnly cookie. Does NOT mask the session (no global overlay). Returns
  // the elevated access token so the caller can re-check /auth/mfa/status.
  stepUpAdminSession: (code: string) => Promise<{ error: Error | null; accessToken?: string }>;
  signUpWithEmail: (email: string, password: string, metadata?: Record<string, string>, next?: string) => Promise<{ error: Error | null; needsConfirmation: boolean }>;
  signInWithOAuth: (provider: "google" | "facebook" | "apple", next?: string) => Promise<{ error: Error | null }>;
  // Starts a signed-in Apple identity link. Unlike signInWithOAuth this never
  // establishes a new principal; it preserves the current session.
  connectApple: () => Promise<{ error: Error | null }>;
  connectGoogle: () => Promise<{ error: Error | null }>;
  signInWithOtp: (
    email: string,
    next?: string,
    opts?: { force?: boolean; journey?: OtpJourneyMetadata; testDelivery?: boolean },
  ) => Promise<{
    error: Error | null;
    alreadySent?: boolean;
    retryAfterSec?: number;
    otpJourneyProof?: string;
    otpTestReceipt?: string;
  }>;
  verifyEmailOtp: (
    email: string,
    token: string,
    opts?: { journey?: OtpJourneyMetadata; journeyProof?: string; testMagicLinkTokenHash?: string },
  ) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useUser(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useUser must be used within an AuthProvider");
  }
  return context;
}
